<template>
  <div
    id="app"
    :style="{ 'background-color': backgroundColor }"
  >
    <cookie-banner />
    <transition
      name="fade"
      mode="out-in"
    >
      <navigation :hide-nav="isMobile && hideNav" />
    </transition>
    <transition
      name="fade"
      mode="out-in"
    >
      <router-view
        :margin="isMobile && !showBanner"
        :promises="promises"
        :slide-progress-bar="isMobile && hideNav"
      />
    </transition>
    <modal>
      <transition
        name="fade"
        mode="out-in"
      >
        <password-recover-form
          :token="token"
          v-if="modalMode === 'logged-out' && (modalStep === 'recover_pass' || modalStep === 'user_activation')"
          :checked-token="checkedToken"
          @remove_token="removeToken()"
        />
        <account-form
          mode="new_account"
          v-if="modalMode === 'logged-out' && modalStep === 'new_account'"
        />
        <car-specs-first-step v-if="modalStep === 'first_step'" />
        <car-specs-second-step v-if="modalStep === 'second_step'" />
        <car-created v-if="modalMode === 'logged-in' && modalStep === 'third_step'" />
        <cancel-visit v-if="modalMode === 'logged-in' && modalStep === 'cancel_visit'" />
        <change-partner
          :partners="modalContent.partners"
          :classes="modalContent.classes"
          :change-favourite="modalContent.changeFavourite"
          v-if="modalStep === 'change_partner'"
        />
        <car-mileage v-if="modalStep === 'car_mileage'" />
        <visit-cancellation v-if="modalStep === 'visit_cancellation'" />
      </transition>
    </modal>
    <div class="vgp-footer">
      <span class="vgp-footer-title">
        © Volkswagen Group Polska
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PasswordRecoverForm from '~/PasswordRecoverForm';
import CarSpecsFirstStep from '~/CarSpecsFirstStep';
import CarSpecsSecondStep from '~/CarSpecsSecondStep';
import CarCreated from '~/CarCreated';
import AccountForm from '~/AccountForm';
import CancelVisit from '~/CancelVisit';
import VisitCancellation from '~/VisitCancellation';
import Modal from '~/Modal';
import ChangePartner from '~/ChangePartner';
import CarMileage from '~/CarMileage';
import Navigation from '~/Navigation';
import CookieBanner from '~/CookieBanner';
import Vue from 'vue';
import ajax from '@/helpers/ajax';
import config from '@/config';

export default {
  components: {
    Modal,
    CarSpecsFirstStep,
    CarSpecsSecondStep,
    PasswordRecoverForm,
    CarCreated,
    CancelVisit,
    ChangePartner,
    CarMileage,
    Navigation,
    AccountForm,
    CookieBanner,
    VisitCancellation,
  },
  data() {
    return {
      isFBReady: false,
      slot: 0,
      loading: true,
      token: null,
      checkedToken: false,
      errorMsg: '',
      scrollPos: 0,
      hideNav: false,
      showBanner: true,
    };
  },
  computed: {
    ...mapGetters({
      fonts: 'brand/getFonts',
      modalStep: 'modal/getModalStep',
      modalMode: 'modal/getModalMode',
      footerColor: 'brand/getFooterColor',
      modalContent: 'modal/getContent',
      authStatus: 'auth/getAuthStatus',
    }),
    isMobile() {
      return this.$mq === 'mobile' || this.$mq === 'xs';
    },
    promises() {
      return [this.getBrandModels(), this.fetchServiceStations()];
    },
    backgroundColor() {
      if (this.$route.name !== undefined && this.$route.name.includes('LoginPage')) {
        return 'black';
      }
      return 'white';
    },
  },
  methods: {
    ...mapActions({
      clearAlert: 'alert/clear',
      getBrandModels: 'brand/getBrandModels',
      resetCarSpecs: 'car/resetCarSpecs',
      fetchServiceStations: 'serviceStations/fetchServiceStations',
      closeModal: 'modal/closeModal',
      toggleModal: 'modal/openModal',
      setCallback: 'modal/setCallback',
      setModalStep: 'modal/setModalStep',
      setModalMode: 'modal/setModalMode',
      setModalContent: 'modal/setContent',
      logout: 'auth/logout',
    }),
    handleScroll() {
      if (this.isMobile) {
        if ((document.body.getBoundingClientRect()).top > this.scrollPos) {
          this.hideNav = false;
        } else {
          this.hideNav = true;
        }

        this.scrollPos = (document.body.getBoundingClientRect()).top;
      }
    },
    onFBSdkReady() {
      this.isFBReady = true;
    },
    checkAccess() {
      const isUserActivation = this.$route.path.includes('user_activation');
      const params = {
        token: this.token,
      };

      ajax.get(config.apiEndpoints.passToken, { params })
        .then((res) => {
          if (res.status === 200) {
            this.checkedToken = true;
            if (isUserActivation) {
              this.logout(true);
              this.userActivationModal();
            } else {
              this.passRecoverModal();
            }
          }
        })
        .catch((error) => {
          this.errorMsg = error.response.data.message;
        });
    },
    passRecoverModal() {
      this.setModalMode('logged-out');
      this.setModalStep('recover_pass');
      this.setModalContent({
        title: 'Resetowanie hasła',
      });
      this.toggleModal();
    },
    visitCancelModal() {
      this.setModalStep('visit_cancellation');
      this.setModalContent({
        title: 'Odwołaj wizytę',
      });
      this.toggleModal();
    },
    userActivationModal() {
      this.setModalMode('logged-out');
      this.setModalStep('user_activation');
      this.setModalContent({
        title: 'Aktywacja konta',
      });
      this.toggleModal();
    },
    removeToken() {
      this.token = null;
      this.checkedToken = false;
    },
  },
  watch: {
    token() {
      if (!this.$route.path.includes('visit_cancellation')) {
        this.checkAccess();
      }
    },
    '$route.path': function () {
      if (this.$route.path.includes('visit_cancellation')) {
        this.visitCancelModal();
      }

      if (this.$route.path.includes('user_activation')) {
        this.userActivationModal();
      }

      if (this.isMobile) {
        window.scrollTo(0, 0);
      }
    },
  },
  mounted() {
    this.isFBReady = Vue.FB !== undefined;
    window.addEventListener('fb-sdk-ready', this.onFBSdkReady);

    if (this.$route.params.access_token) {
      this.token = this.$route.params.access_token;
    }

    if (this.$route.path.includes('visit_cancellation')) {
      this.visitCancelModal();
    }

    Promise.all(this.promises);
  },
  created() {
    this.clearAlert();
  },
};
</script>
